import React, {useEffect, useLayoutEffect, useCallback, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Box, Flex, HStack, VStack, IconButton, Link as ChakraLink, useTheme, keyframes, Button} from '@chakra-ui/react';
import {Link as RouterLink} from 'react-router-dom';
import styled from 'styled-components';
import {GlobalBannerActions, GlobalBannerNotification, GlobalBannerState, GlobalBannerAction, GlobalBannerLink} from './GlobalBannerSlice';
import {TextG14, TextG12} from '../../components/typography';
import {BlueButton, WhiteButton} from '../../components/button';
import {useBannerActionHandler, addDismissedBanner} from './GlobalBannerManager';

const slideDown = keyframes`
  from {
    transform: translateY(-100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const BannerContainer = styled(Box)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  animation: ${slideDown} 0.3s ease-out;
`;

const getVariantColors = (variant: string, theme: any) => {
  switch (variant) {
    case 'success':
      return {bg: '#276ACD', text: 'white', buttonBg: 'white', buttonText: '#276ACD'};
    case 'warning':
      return {bg: '#F5A623', text: 'white', buttonBg: 'white', buttonText: '#F5A623'};
    case 'error':
      return {bg: '#F5737F', text: 'white', buttonBg: 'white', buttonText: '#F5737F'};
    case 'neutral':
      return {bg: '#E8E8E8', text: '#262626', buttonBg: '#C9136B', buttonText: 'white'};
    case 'info':
    default:
      return {bg: '#E8E8E8', text: '#262626', buttonBg: '#C9136B', buttonText: 'white'};
  }
};

type BannerItemProps = {
  notification: GlobalBannerNotification;
  onDismiss: (id: string) => void;
};

const BannerItem: React.FC<BannerItemProps> = ({notification, onDismiss}) => {
  const theme = useTheme();
  const {bg, text, buttonBg, buttonText} = getVariantColors(notification.variant || 'info', theme);
  const handleBannerAction = useBannerActionHandler();

  // Handle auto-dismiss based on durationSeconds
  useEffect(() => {
    // If durationSeconds is null, undefined, or 0, don't auto-dismiss
    if (notification.durationSeconds == null || notification.durationSeconds <= 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      onDismiss(notification.id);
    }, notification.durationSeconds * 1000);

    return () => clearTimeout(timeoutId);
  }, [notification.id, notification.durationSeconds, onDismiss]);

  const handleActionClick = (action: GlobalBannerAction, e: React.MouseEvent) => {
    e.stopPropagation();
    // Use the actionId to look up and call the handler
    if (action.actionId) {
      handleBannerAction(action.actionId);
    } else {
      // Just dismiss if no action handler
      onDismiss(notification.id);
    }
  };

  const renderLink = (link: GlobalBannerLink, index: number) => {
    if (link.isExternal) {
      return (
        <ChakraLink
          key={index}
          href={link.url}
          isExternal
          color={text}
          textDecoration="underline"
          fontWeight="600"
          fontSize="14px"
          _hover={{opacity: 0.8}}>
          {link.label}
        </ChakraLink>
      );
    }
    return (
      <ChakraLink
        key={index}
        as={RouterLink}
        to={link.url}
        color={text}
        textDecoration="underline"
        fontWeight="600"
        fontSize="14px"
        _hover={{opacity: 0.8}}>
        {link.label}
      </ChakraLink>
    );
  };

  const renderAction = (action: GlobalBannerAction, index: number) => {
    if (action.variant === 'secondary' || action.variant === 'outline') {
      return (
        <Button
          key={index}
          onClick={(e) => handleActionClick(action, e)}
          variant="outline"
          px={4}
          py={2}
          height="auto"
          borderRadius="full"
          borderColor={buttonBg}
          color={buttonBg}
          bg="transparent"
          _hover={{opacity: 0.8}}
          _active={{transform: 'scale(0.98)'}}>
          <TextG14 fontWeight="500">{action.label}</TextG14>
        </Button>
      );
    }

    return (
      <Button
        key={index}
        onClick={(e) => handleActionClick(action, e)}
        bg={buttonBg}
        color={buttonText}
        px={5}
        py={2}
        height="auto"
        borderRadius="full"
        _hover={{opacity: 0.9, bg: buttonBg}}
        _active={{transform: 'scale(0.98)'}}>
        <TextG14 fontWeight="500">{action.label}</TextG14>
      </Button>
    );
  };

  return (
    <Box bg={bg} px={5} py={4} borderBottom="1px solid" borderColor="#D0D0D0">
      <Flex align="center" justify="space-between" gap={3}>
        <TextG14 color={text} fontWeight="500" flex="1">
          {notification.text}
        </TextG14>

        <HStack spacing={3} flexShrink={0}>
          {notification.links && notification.links.length > 0 &&
            notification.links.map((link, index) => renderLink(link, index))}

          {notification.actions &&
            notification.actions.map((action, index) => renderAction(action, index))}

          {notification.dismissible === true && (
            <IconButton
              aria-label="Dismiss banner"
              icon={
                <Box as="span" fontSize="16px" color={text}>
                  ✕
                </Box>
              }
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onDismiss(notification.id);
              }}
              _hover={{opacity: 0.8}}
              minW="28px"
              h="32px"
            />
          )}
        </HStack>
      </Flex>
    </Box>
  );
};

const GlobalBanner: React.FC = () => {
  const dispatch = useDispatch();
  const bannerRef = useRef<HTMLDivElement>(null);
  const notifications = useSelector(
    (state: {globalBanner: GlobalBannerState}) => state.globalBanner?.notifications || []
  );

  const handleDismiss = useCallback(
    (id: string) => {
      // Mark as permanently dismissed so it won't reappear across sessions
      addDismissedBanner(id);
      dispatch(GlobalBannerActions.hideBanner(id));
    },
    [dispatch]
  );

  // Measure banner height and dispatch to Redux for TopBar offset
  // Use useLayoutEffect to measure before paint, preventing visual flash
  useLayoutEffect(() => {
    const updateHeight = () => {
      if (bannerRef.current && notifications.length > 0) {
        dispatch(GlobalBannerActions.setBannerHeight(bannerRef.current.offsetHeight));
      }
    };

    updateHeight();

    // Update on window resize
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [notifications, dispatch]);

  // Reset height when component unmounts
  useEffect(() => {
    return () => {
      dispatch(GlobalBannerActions.setBannerHeight(0));
    };
  }, [dispatch]);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <BannerContainer ref={bannerRef}>
      {notifications.map((notification) => (
        <BannerItem key={notification.id} notification={notification} onDismiss={handleDismiss} />
      ))}
    </BannerContainer>
  );
};

export default GlobalBanner;
